import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { FileText, Upload, Trash2, Loader2, File, Image, FileCheck } from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { value: 'license', label: 'Driver License' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'vehicle_rego', label: 'Vehicle Registration' },
  { value: 'medical', label: 'Medical Certificate' },
  { value: 'certification', label: 'Certification' },
  { value: 'other', label: 'Other' },
];

const DOC_TYPE_ICONS = {
  license: FileCheck, insurance: FileText, vehicle_rego: File,
  medical: FileText, certification: FileCheck, other: File,
};

const DriverDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState('other');

  const token = localStorage.getItem('driver_token');

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const result = await safeFetch(`${API_URL}/api/go/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (result.ok) setDocuments(result.data.documents || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', selectedType);

      const response = await fetch(`${API_URL}/api/go/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        fetchDocuments();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (docId, filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    try {
      await safeFetch(`${API_URL}/api/go/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchDocuments();
    } catch (err) {
      console.error(err);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
      timeZone: 'Australia/Melbourne'
    });
  };

  const isImage = (name) => /\.(jpg|jpeg|png|gif|webp)$/i.test(name);

  return (
    <div className="space-y-6" data-testid="driver-documents">
      <div>
        <h1 className="text-2xl font-bold text-white">Documents</h1>
        <p className="text-[#64748B] text-sm mt-1">Upload and manage your documents</p>
      </div>

      {/* Upload Section */}
      <Card className="border-0 bg-[#1E293B]">
        <CardContent className="p-5">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="text-xs font-medium text-[#94A3B8] mb-1.5 block uppercase tracking-wide">Document Type</label>
              <select
                value={selectedType}
                onChange={e => setSelectedType(e.target.value)}
                className="w-full rounded-lg border border-[#334155] bg-[#0F172A] px-3 py-2.5 text-sm text-white"
                data-testid="doc-type-select"
              >
                {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <input
                type="file"
                id="doc-upload"
                className="hidden"
                onChange={handleUpload}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                data-testid="doc-file-input"
              />
              <Button
                onClick={() => document.getElementById('doc-upload').click()}
                disabled={uploading}
                className="bg-[#10B981] hover:bg-[#059669] text-white h-10"
                data-testid="upload-doc-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#10B981]" /></div>
      ) : documents.length === 0 ? (
        <Card className="border-0 bg-[#1E293B]">
          <CardContent className="py-16 text-center">
            <FileText className="w-12 h-12 text-[#334155] mx-auto mb-3" />
            <p className="text-[#64748B]">No documents uploaded yet</p>
            <p className="text-[#475569] text-xs mt-1">Upload your license, insurance, and other documents</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {documents.map(doc => {
            const DocIcon = DOC_TYPE_ICONS[doc.doc_type] || File;
            const typeLabel = DOC_TYPES.find(t => t.value === doc.doc_type)?.label || doc.doc_type;

            return (
              <Card key={doc.id} className="border-0 bg-[#1E293B]" data-testid={`doc-${doc.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#10B981]/10 flex items-center justify-center flex-shrink-0">
                      {isImage(doc.filename) ? (
                        <Image className="w-5 h-5 text-[#10B981]" />
                      ) : (
                        <DocIcon className="w-5 h-5 text-[#10B981]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{doc.filename}</p>
                      <div className="flex items-center gap-3 text-xs text-[#64748B] mt-0.5">
                        <span className="px-1.5 py-0.5 rounded bg-[#334155] text-[#94A3B8]">{typeLabel}</span>
                        <span>{formatSize(doc.size)}</span>
                        <span>{formatDate(doc.uploaded_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`${API_URL}${doc.url}`, '_blank')}
                        className="text-[#94A3B8] hover:text-[#10B981] hover:bg-[#10B981]/5"
                        data-testid={`view-doc-${doc.id}`}
                      >
                        View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(doc.id, doc.filename)}
                        className="text-[#94A3B8] hover:text-red-400 hover:bg-red-500/5"
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DriverDocuments;
